import React from 'react';
import { useLoaderData, useNavigate } from 'react-router-dom';
import { SEOHead } from '@/components/SEOHead';
import { LocationBrowser } from '@/components/locations/LocationBrowser';
import { LocationIndex } from '@/components/locations/LocationIndex';
import type { LocationSummary } from '@/lib/locations';

export default function Locations() {
  const navigate = useNavigate();
  // Read from the committed snapshot at build time, so the cards are in the HTML (ERI-2594).
  const locations = useLoaderData() as LocationSummary[] | undefined;

  return (
    <>
      <SEOHead
        title="Dark Sky Locations for Astrophotography | Milky Way Planner"
        description="Discover 400+ dark sky locations for Milky Way photography. Browse by Bortle class, best months, and type, with a free visibility calendar."
        canonicalUrl="/dark-sky-locations"
      />
      <div className="min-h-screen bg-background">
        {/* Hero */}
        <section className="bg-[#0f1923] text-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20 text-center">
            <p className="text-gold uppercase text-sm font-semibold tracking-[0.14em] mb-4">
              Dark Sky Directory
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl font-bold tracking-tight mb-4 leading-[1.1]">
              Dark Sky Locations
            </h1>
            <p className="text-white/70 text-lg max-w-2xl mx-auto mb-8 leading-relaxed">
              Explore the world's best dark sky sites for Milky Way photography. Each location includes visibility data, Bortle class, and a free shooting calendar.
            </p>
            <a href="#all-locations" className="text-sm font-semibold text-gold hover:text-gold-light transition-colors">
              See the full list by country and US state
            </a>
          </div>
        </section>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
          <LocationBrowser
            onLocationSelect={(loc) => navigate(`/dark-sky-locations/${loc.slug}`)}
            initialLocations={locations}
            showMap={true}
          />
          {/* Every location as a plain link, so search engines can reach all of them (ERI-2853). */}
          {locations && locations.length > 0 && <LocationIndex locations={locations} />}
        </div>
      </div>
    </>
  );
}
